/**
 * Image Quality Check — runs before upload to detect blur, dark, or overexposed images.
 * Returns a quality assessment with pass/warn/fail and suggestions.
 */

export function checkImageQuality(base64Image) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 256;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, size, size);
      const imageData = ctx.getImageData(0, 0, size, size);
      const pixels = imageData.data;

      // 1. Brightness check
      let totalBrightness = 0;
      const pixelCount = pixels.length / 4;
      for (let i = 0; i < pixels.length; i += 4) {
        totalBrightness += (pixels[i] * 0.299 + pixels[i + 1] * 0.587 + pixels[i + 2] * 0.114);
      }
      const avgBrightness = totalBrightness / pixelCount;

      // 2. Contrast check (standard deviation of brightness)
      let sumSquares = 0;
      for (let i = 0; i < pixels.length; i += 4) {
        const lum = pixels[i] * 0.299 + pixels[i + 1] * 0.587 + pixels[i + 2] * 0.114;
        sumSquares += (lum - avgBrightness) ** 2;
      }
      const contrast = Math.sqrt(sumSquares / pixelCount);

      // 3. Blur check (Laplacian variance on grayscale)
      const gray = new Float32Array(size * size);
      for (let i = 0; i < pixelCount; i++) {
        gray[i] = pixels[i * 4] * 0.299 + pixels[i * 4 + 1] * 0.587 + pixels[i * 4 + 2] * 0.114;
      }

      let laplacianVar = 0;
      let laplacianCount = 0;
      for (let y = 1; y < size - 1; y++) {
        for (let x = 1; x < size - 1; x++) {
          const idx = y * size + x;
          const lap = gray[idx - size] + gray[idx + size] + gray[idx - 1] + gray[idx + 1] - 4 * gray[idx];
          laplacianVar += lap * lap;
          laplacianCount++;
        }
      }
      const sharpness = laplacianVar / laplacianCount;

      // Build result
      const issues = [];
      let quality = "good";

      if (avgBrightness < 40) {
        issues.push("Image is too dark — use better lighting or flash");
        quality = "poor";
      } else if (avgBrightness < 70) {
        issues.push("Image is slightly dark — consider better lighting");
        if (quality !== "poor") quality = "fair";
      } else if (avgBrightness > 220) {
        issues.push("Image is overexposed — reduce lighting or avoid direct flash");
        quality = "poor";
      } else if (avgBrightness > 200) {
        issues.push("Image is slightly bright");
        if (quality !== "poor") quality = "fair";
      }

      if (contrast < 20) {
        issues.push("Very low contrast — image appears washed out or foggy");
        quality = "poor";
      } else if (contrast < 35) {
        issues.push("Low contrast — details may be hard to distinguish");
        if (quality !== "poor") quality = "fair";
      }

      if (sharpness < 50) {
        issues.push("Image is very blurry — hold camera steady and tap to focus");
        quality = "poor";
      } else if (sharpness < 150) {
        issues.push("Image may be slightly blurry — try tapping to focus before capturing");
        if (quality !== "poor") quality = "fair";
      }

      resolve({
        quality,
        brightness: Math.round(avgBrightness),
        contrast: Math.round(contrast),
        sharpness: Math.round(sharpness),
        issues,
        canProceed: quality !== "poor",
        suggestions: issues.length === 0
          ? "Image quality looks good!"
          : issues.join(". ") + "."
      });
    };

    img.onerror = () => {
      resolve({ quality: "unknown", issues: ["Could not analyze image"], canProceed: true, suggestions: "" });
    };

    img.src = base64Image.startsWith("data:") ? base64Image : `data:image/jpeg;base64,${base64Image}`;
  });
}
